import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import '../styles/artifactory.css';
import { MiniFileTree } from '../components/artifactory/MiniFileTree';
import { ArtifactoryCommander } from '../components/artifactory/ArtifactoryCommander';
import { ArtifactoryChat } from '../components/artifactory/ArtifactoryChat';
import { FileTree } from '../components/artifactory/FileTree';
import { NodeInspector } from '../components/artifactory/NodeInspector';
import { ScaffoldingForm } from '../components/artifactory/ScaffoldingForm';
import { ProjectDetailsPanel } from '../components/artifactory/ProjectDetailsPanel';
import { ProjectStatusLog, StatusLogEntry } from '../components/artifactory/ProjectStatusLog';
import { HydrationPlanPage } from './HydrationPlanPage';
import { useArtifactoryStore } from '../stores/artifactoryStore';
import { useTheme } from '../contexts/ThemeContext';
import * as sessionService from '../services/sessionService';
import { artifactoryService } from '../services/artifactoryService';
import {
  FiCode, FiGitBranch, FiDownload, FiShare2, FiArrowLeft, FiPlus,
  FiLayout, FiServer, FiSmartphone, FiZap, FiBarChart2, FiClock, FiTag,
  FiSettings, FiCpu
} from 'react-icons/fi';
import { HandoffDialog } from '../components/artifactory/HandoffDialog';
import { PersonaMarkdown } from '../components/personas/PersonaMarkdown';
import { ArtifactoryMarkdown } from '../components/artifactory/ArtifactoryMarkdown';
import { Spinner } from '../components/common/Spinner';
import { BetaBadge } from '../components/common/BetaBadge';
import { IconType } from 'react-icons';

// Define the possible page states
type PageState = 'SESSION_SELECTION' | 'SCAFFOLDING' | 'PROJECT_VIEW';

export const ArtifactoryPage: React.FC = () => {
  // URL and navigation
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Core state
  const [pageState, setPageState] = useState<PageState>('SESSION_SELECTION');
  const [sessions, setSessions] = useState<any[]>([]);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(sessionId || null);
  const [manifestId, setManifestId] = useState<string | null>(null);
  const [sessionData, setSessionData] = useState<any>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [handoffDialogOpen, setHandoffDialogOpen] = useState(false);
  
  // Helper function to get time ago string
  const getTimeAgo = (date: Date): string => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);
    
    if (diffDay > 0) {
      return diffDay === 1 ? '1 day ago' : `${diffDay} days ago`;
    } else if (diffHour > 0) {
      return diffHour === 1 ? '1 hour ago' : `${diffHour} hours ago`;
    } else if (diffMin > 0) {
      return diffMin === 1 ? '1 minute ago' : `${diffMin} minutes ago`;
    } else {
      return 'Just now';
    }
  };
  
  // Helper function to get project icon and color
  const getProjectIconAndColor = (session: any): { icon: IconType, color: string } => {
    // Default values
    let icon = FiCode;
    let color = darkMode ? 'bg-blue-800' : 'bg-blue-600';
    
    // Try to determine project type from metadata or content
    const prompt = session.prompt?.toLowerCase() || '';
    
    if (prompt.includes('react') || prompt.includes('frontend') || prompt.includes('ui')) {
      icon = FiLayout;
      color = darkMode ? 'bg-cyan-800' : 'bg-cyan-600';
    } else if (prompt.includes('api') || prompt.includes('backend') || prompt.includes('server')) {
      icon = FiServer;
      color = darkMode ? 'bg-green-800' : 'bg-green-600';
    } else if (prompt.includes('mobile') || prompt.includes('app') || prompt.includes('android') || prompt.includes('ios')) {
      icon = FiSmartphone;
      color = darkMode ? 'bg-purple-800' : 'bg-purple-600';
    } else if (prompt.includes('game') || prompt.includes('unity') || prompt.includes('3d')) {
      icon = FiZap;
      color = darkMode ? 'bg-red-800' : 'bg-red-600';
    } else if (prompt.includes('data') || prompt.includes('analysis') || prompt.includes('ml') || prompt.includes('ai')) {
      icon = FiBarChart2;
      color = darkMode ? 'bg-yellow-800' : 'bg-yellow-600';
    }
    
    return { icon, color };
  };
  
  // Helper function to get project status
  const getProjectStatus = (session: any): string => {
    // This is a placeholder - in a real app, you'd determine this from actual project state
    const updatedRecently = new Date(session.updated_at).getTime() > Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    if (session.phase === 'completed') {
      return 'Completed';
    } else if (updatedRecently) {
      return 'Active';
    } else {
      return 'Inactive';
    }
  };
  
  // Helper function to get status class for table view
  const getStatusClass = (status: string): string => {
    switch (status) {
      case 'Completed':
        return darkMode 
          ? 'bg-green-900 text-green-300' 
          : 'bg-green-100 text-green-800';
      case 'Active':
        return darkMode 
          ? 'bg-blue-900 text-blue-300' 
          : 'bg-blue-100 text-blue-800';
      default:
        return darkMode 
          ? 'bg-gray-700 text-gray-300' 
          : 'bg-gray-100 text-gray-800';
    }
  };
  
  // Helper function to convert color string to bg class
  const color2BgClass = (colorClass: string): string => {
    // Extract the color name from classes like "bg-blue-600"
    const match = colorClass.match(/bg-(\w+)-\d+/);
    if (match && match[1]) {
      return darkMode ? `bg-${match[1]}-800` : `bg-${match[1]}-600`;
    }
    return darkMode ? 'bg-gray-800' : 'bg-gray-600';
  };
  
  // Loading states
  const [isLoadingSessions, setIsLoadingSessions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Theme
  const { darkMode } = useTheme();
  
  // Artifactory store
  const { manifest, selectedNodeId, selectNode, resetStore } = useArtifactoryStore();
  
  // Add logging to verify the selectNode function
  useEffect(() => {
    console.log("Artifactory component mounted");
    console.log("selectNode function available:", !!selectNode);
  }, []);

  // Check for selectedNodeId in localStorage
  useEffect(() => {
    const savedNodeId = localStorage.getItem('artifactory_selectedNodeId');
    if (savedNodeId) {
      console.log("Found saved selectedNodeId in localStorage:", savedNodeId);
    }
  }, []);

  // If navigated here with state.startNew, immediately kick off new project creation.
  // Watch location.state?.startNew so this fires even when the component is already
  // mounted (e.g. user is already on /artifactory and clicks "+ New Blueprint" again).
  useEffect(() => {
    if (location.state?.startNew) {
      console.log("[Artifactory] startNew detected in location state — creating new project");
      // Clear the state so a refresh doesn't re-trigger
      window.history.replaceState({}, document.title);
      handleCreateNewProject();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.startNew]);

  // Initialize state based on URL parameters
  useEffect(() => {
    const initializeFromUrl = async () => {
      console.log("initializeFromUrl called, sessionId from URL:", sessionId);
      // Reset any error state
      setError(null);
      
      // If we have a session ID in the URL
      if (sessionId) {
        setIsLoadingSessions(true);
        try {
          // Set the selected session ID
          setSelectedSessionId(sessionId);

          // First, check if this ID is directly a manifest ID (post-nullable session_id era)
          try {
            const manifest = await artifactoryService.getManifest(sessionId);
            if (manifest) {
              console.log(`URL ID ${sessionId} is a manifest ID directly`);
              setManifestId(sessionId);
              // Note: sessionId here is the manifest artifact UUID, not a real session.
              // The backend hydration/fabrication/mutation endpoints will auto-bootstrap
              // an ephemeral session for this ID if it doesn't exist in the session manager.
              setSelectedSessionId(sessionId);
              setSessionData({
                prompt: manifest.root_context || '',
                created_at: manifest.created_at,
                updated_at: manifest.updated_at,
                tags: []
              });
              setPageState('PROJECT_VIEW');
              setIsLoadingSessions(false);
              return;
            }
          } catch {
            // Not a manifest ID, fall through to session lookup
          }

          // Fall back: treat as a session ID
          try {
            const sessionDetails = await sessionService.getSession(sessionId);
            setSessionData(sessionDetails);

            // Check if this session already has a manifest
            try {
              const manifestInfo = await artifactoryService.getSessionManifest(sessionId);
              if (manifestInfo.has_manifest && manifestInfo.manifest_id) {
                console.log(`Found existing manifest for session ${sessionId}: ${manifestInfo.manifest_id}`);
                setManifestId(manifestInfo.manifest_id);

                // Load the manifest to get the intent
                const manifest = await artifactoryService.getManifest(manifestInfo.manifest_id);

                if ((!sessionDetails.prompt || sessionDetails.prompt.trim() === '') && manifest.root_context) {
                  setSessionData(prev => ({
                    ...prev,
                    prompt: manifest.root_context
                  }));
                }

                setPageState('PROJECT_VIEW');
              } else {
                setPageState('SCAFFOLDING');
              }
            } catch (error) {
              console.error("Failed to check for existing manifests:", error);
              setError("Failed to load project data. Please try again.");
              setPageState('SESSION_SELECTION');
            }
          } catch (error) {
            console.error("Failed to load session:", error);
            setError("Failed to load session. Please try again.");
            setPageState('SESSION_SELECTION');
          }
        } finally {
          setIsLoadingSessions(false);
        }
      } else {
        // No session ID, show session selection
        setPageState('SESSION_SELECTION');
        loadSessions();
      }
    };
    
    initializeFromUrl();
  }, [sessionId]);
  
  // Effect to ensure file tree is expanded when project is loaded
  useEffect(() => {
    if (manifest && selectedNodeId) {
      // Force expansion of the path to the selected node
      const node = manifest.nodes[selectedNodeId];
      if (node) {
        console.log("Ensuring path to selected node is expanded:", node.path);
        useArtifactoryStore.getState().expandPathToNode(node.path);
      }
      
      // Also expand root directories for better visibility
      Object.values(manifest.nodes)
        .filter(node => node.type === 'directory' && node.path.split('/').length <= 2)
        .forEach(node => {
          useArtifactoryStore.getState().expandPathToNode(node.path);
        });
    }
  }, [manifest?.id, selectedNodeId]); // Only run when manifest ID or selected node changes

  // Load available sessions
  const loadSessions = async () => {
    setIsLoadingSessions(true);
    setError(null);
    try {
      // Use the new artifactory-specific endpoint
      const data = await artifactoryService.listArtifactoryProjects();
      setSessions(data);
    } catch (error) {
      console.error("Failed to load artifactory projects:", error);
      setError("Failed to load projects. Please try again.");
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Handle session selection
  const handleSessionSelect = (id: string) => {
    console.log("handleSessionSelect called with id:", id);
    // Navigate to the session page
    console.log("Navigating to:", `/artifactory/${id}`);
    navigate(`/artifactory/${id}`);
  };
  
  // Handle sorting
  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Sort sessions based on current sort field and direction
  const sortedSessions = React.useMemo(() => {
    if (!sortField || !sessions.length) return sessions;
    
    return [...sessions].sort((a, b) => {
      let valueA, valueB;
      
      if (sortField === 'name') {
        // Sort by project name
        valueA = a.project_name || (a.prompt ? a.prompt.substring(0, 30) : 'Untitled Project');
        valueB = b.project_name || (b.prompt ? b.prompt.substring(0, 30) : 'Untitled Project');
        return sortDirection === 'asc' 
          ? valueA.localeCompare(valueB) 
          : valueB.localeCompare(valueA);
      } 
      else if (sortField === 'created') {
        // Sort by creation date
        valueA = new Date(a.created_at).getTime();
        valueB = new Date(b.created_at).getTime();
      }
      else if (sortField === 'hydration') {
        // Calculate hydration percentage for sorting
        const getHydrationPercentage = (session: any) => {
          const stats = {
            total: 0,
            fresh: 0
          };
          
          if (session.manifest_content?.nodes) {
            Object.values(session.manifest_content.nodes).forEach((node: any) => {
              if (node.type === 'file') {
                stats.total++;
                if (node.status === 'fresh') stats.fresh++;
              }
            });
          }
          
          return stats.total > 0 ? Math.round((stats.fresh / stats.total) * 100) : 0;
        };
        
        valueA = getHydrationPercentage(a);
        valueB = getHydrationPercentage(b);
      }
      else {
        return 0;
      }
      
      // For numeric values
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      }
      
      // Default string comparison
      return sortDirection === 'asc' 
        ? String(valueA).localeCompare(String(valueB)) 
        : String(valueB).localeCompare(String(valueA));
    });
  }, [sessions, sortField, sortDirection]);

  // Handle new project creation
  const handleCreateNewProject = async () => {
    try {
      console.log("[Artifactory] handleCreateNewProject called");
      // Show loading state
      setIsLoadingSessions(true);
      setError(null);
      
      // Create a new session with a default prompt for Artifactory
      const result = await sessionService.createSession("New Artifactory Project");
      
      // Navigate directly to the artifactory page with the new session ID
      navigate(`/artifactory/${result.session_id}`);
    } catch (error) {
      console.error("Failed to create new session:", error);
      setError("Failed to create new session. Please try again.");
    } finally {
      setIsLoadingSessions(false);
    }
  };

  // Handle new artifactory project
  const handleNewArtifactoryProject = () => {
    console.log("New Artifactory Project button clicked - RESETTING EVERYTHING");
    // Reset all state
    resetStore();
    setSelectedSessionId(null);
    setManifestId(null);
    setSessionData(null);
    
    // Navigate to the artifactory page
    navigate('/artifactory');
    
    // Set page state to session selection
    setPageState('SESSION_SELECTION');
  };
  
  // Toggle card flip
  const toggleCardFlip = (sessionId: string, e: React.MouseEvent) => {
    console.log("toggleCardFlip called for session:", sessionId);
    e.stopPropagation(); // Prevent card click from navigating
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sessionId)) {
        console.log("Removing session from flipped cards:", sessionId);
        newSet.delete(sessionId);
      } else {
        console.log("Adding session to flipped cards:", sessionId);
        newSet.add(sessionId);
      }
      return newSet;
    });
  };
  
  // Handle clicks outside flipped cards to flip them back
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      // If we have any flipped cards and the click is outside those cards, flip them back
      if (flippedCards.size > 0) {
        const clickedOnFlippedCard = Array.from(flippedCards).some(id => {
          const cardElement = document.getElementById(`card-${id}`);
          return cardElement && cardElement.contains(e.target as Node);
        });
        
        if (!clickedOnFlippedCard) {
          setFlippedCards(new Set());
        }
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [flippedCards]);

  // Handle scaffolding completion
  const handleScaffoldingComplete = (id: string) => {
    // Set the manifest ID
    setManifestId(id);
    
    // Update URL with manifest ID
    navigate(`/artifactory/${selectedSessionId}?manifestId=${id}`);
    
    // Set page state to project view
    setPageState('PROJECT_VIEW');
  };

  // Add status log state
  const [statusLogs, setStatusLogs] = useState<StatusLogEntry[]>([]);

  // Add function to add log entries
  const addStatusLog = (entry: Omit<StatusLogEntry, 'id' | 'timestamp'>) => {
    const newEntry: StatusLogEntry = {
      ...entry,
      id: Date.now().toString(),
      timestamp: new Date()
    };
    
    setStatusLogs(prev => [newEntry, ...prev]);
  };

  // Handle intent update
  const handleUpdateIntent = async (newIntent: string): Promise<void> => {
    try {
      console.log("Updating project with new intent:", newIntent);
      // Don't set global loading state here
      
      // First, update the session data locally for immediate UI feedback
      setSessionData(prev => ({
        ...prev,
        prompt: newIntent,
        updated_at: new Date().toISOString()
      }));
      
      // Add a status log entry for the intent update
      addStatusLog({
        type: 'info',
        message: 'Updating project structure...',
        details: `Updating project based on new intent: "${newIntent.substring(0, 50)}${newIntent.length > 50 ? '...' : ''}"`
      });
      
      // Make the actual API call to update the project structure
      if (manifestId && selectedSessionId) {
        const result = await artifactoryService.mutateProject(manifestId, selectedSessionId, newIntent);
      
        // After successful update, refresh the manifest to get the latest structure
        await useArtifactoryStore.getState().refreshManifest();
        
        // Add a success log with affected files
        addStatusLog({
          type: 'success',
          message: `Project structure updated successfully`,
          details: `Updated ${result.updated_nodes_count} nodes, added ${result.new_nodes_count} new nodes, and deleted ${result.deleted_nodes_count} nodes.`,
          affectedFiles: result.affected_files || []
        });
        
        // Ensure the selected node is still selected after refresh
        const { selectedNodeId } = useArtifactoryStore.getState();
        if (selectedNodeId) {
          // Re-select the node to maintain selection
          useArtifactoryStore.getState().selectNode(selectedNodeId);
        }
      }
      
      return Promise.resolve();
    } catch (error) {
      console.error("Error updating project intent:", error);
      
      // Add an error log
      addStatusLog({
        type: 'error',
        message: 'Failed to update project structure',
        details: error.message || 'Unknown error occurred'
      });
      
      return Promise.reject(error);
    } finally {
      // Don't reset global loading state here
    }
  };

  // Render loading state
  if (isLoadingSessions) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-lg">Loading sessions...</p>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="w-full">
        <div className={`p-4 rounded-md ${darkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-red-100 text-red-700'}`}>
          <h2 className="text-lg font-semibold mb-2">Error</h2>
          <p>{error}</p>
          <button 
            onClick={handleNewArtifactoryProject}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Return to Artifactory Home
          </button>
        </div>
      </div>
    );
  }

  // Render based on page state
  switch (pageState) {
    case 'SESSION_SELECTION':
      return (
        <div className={`w-full ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4 relative">
            {/* Add floating background effect */}
            <style jsx>{`
              .header-container {
                position: relative;
              }
              
              .header-container::before {
                content: '';
                position: absolute;
                top: -10px;
                left: -10px;
                right: -10px;
                bottom: -10px;
                background: ${darkMode ? 'rgba(15, 23, 42, 0.7)' : 'rgba(255, 255, 255, 0.7)'};
                border-radius: 12px;
                z-index: 0;
                box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(59, 130, 246, 0.2);
                backdrop-filter: blur(8px);
                animation: pulse 4s infinite;
              }
              
              @keyframes pulse {
                0%, 100% {
                  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1), 0 4px 8px rgba(59, 130, 246, 0.2);
                }
                50% {
                  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(59, 130, 246, 0.3);
                }
              }
            `}</style>
            <div className="header-container w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div className="relative z-10">
                <h1 className="text-3xl font-bold flex items-center">
                  <div className={`mr-3 p-2 rounded-lg ${
                    darkMode 
                      ? 'bg-gradient-to-br from-blue-900 to-purple-900 shadow-inner shadow-blue-700/30' 
                      : 'bg-gradient-to-br from-blue-100 to-purple-100 shadow-inner shadow-blue-300/30'
                  }`}>
                    <FiCode className={`h-6 w-6 ${darkMode ? 'text-blue-300' : 'text-blue-700'}`} />
                  </div>
                  <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-600 drop-shadow-sm">
                    Artifactory
                  </span>
                </h1>
                <p className={`mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'} max-w-xl`}>
                  Create, manage and generate complete project scaffoldings with AI-powered architects and specialists guiding the planning and implementation
                </p>
              </div>
              
              {/* New Blueprint button */}
              <div className="group relative z-10">
                <button 
                  onClick={handleCreateNewProject}
                  className={`
                    ${darkMode 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700' 
                      : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600'
                    } 
                    text-white px-5 py-2.5 rounded-md shadow-md hover:shadow-lg 
                    transition-all duration-200 flex items-center space-x-2
                    hover:shadow-blue-500/50 dark:hover:shadow-blue-800/30
                    font-bold
                  `}
                >
                  <FiPlus className="mr-2" />
                  New Blueprint
                  <span className="ml-2"><BetaBadge /></span>
                </button>
                <div className={`absolute top-full right-0 mt-2 px-3 py-2 rounded shadow-lg z-10 
                  text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none
                  ${darkMode ? 'bg-gray-900 text-gray-200' : 'bg-white text-gray-800'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}
                  whitespace-normal max-w-md w-80`}>
                  <PersonaMarkdown content={`
## Artifactory Project

Create AI-powered code projects with:

- **Project Structure**: Generate complete, well-organized codebases
- **Intelligent Dependencies**: Files that understand their relationships
- **Hydration System**: Generate code with context from dependencies
- **Export Options**: Download your complete project as a ZIP file

Artifactory helps you rapidly prototype applications, libraries, or components with proper architecture and best practices.
                  `} size="small" />
                  <div className={`absolute bottom-full right-8 border-4 border-transparent 
                    ${darkMode ? 'border-b-gray-900' : 'border-b-white'}`}></div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-500">Your Blueprints</h2>
              <div className="text-xs text-gray-500">
                Showing {sessions.length} blueprints
              </div>
            </div>
            
            {/* View toggle buttons */}
            <div className="flex rounded-md shadow-sm" role="group">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === 'grid'
                    ? (darkMode ? 'bg-blue-600 text-white shadow-inner' : 'bg-blue-500 text-white shadow-inner')
                    : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
                } ${viewMode === 'grid' ? '' : 'hover:bg-gray-300'} rounded-l-lg transition-all duration-200`}
              >
                Grid
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`px-4 py-2 text-sm font-medium ${
                  viewMode === 'table'
                    ? (darkMode ? 'bg-blue-600 text-white shadow-inner' : 'bg-blue-500 text-white shadow-inner')
                    : (darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700')
                } ${viewMode === 'table' ? '' : 'hover:bg-gray-300'} rounded-r-lg transition-all duration-200`}
              >
                Table
              </button>
            </div>
          </div>
          
          {sessions.length > 0 ? (
            viewMode === 'grid' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {sessions.map((session) => {
                // Extract project name from session data
                const projectName = session.project_name || 
                                   (session.prompt ? session.prompt.substring(0, 30) + '...' : 'Untitled Project');
            
                // Calculate time since creation for display
                const createdDate = new Date(session.created_at);
                const timeAgo = getTimeAgo(createdDate);
            
                // Determine project icon and color based on metadata or content
                const { icon: ProjectIcon, color: projectColor } = getProjectIconAndColor(session);
            
                return (
                  <div 
                    id={`card-${session.id}`}
                    key={session.id} 
                    className={`persona-card card-container border rounded-lg overflow-hidden cursor-pointer transition-all duration-300 transform hover:scale-[1.02] hover:translate-y-[-4px] ${
                      darkMode 
                        ? 'bg-gray-800 text-white border-gray-700 hover:border-blue-400 hover:shadow-lg hover:shadow-blue-900/20' 
                        : 'bg-white text-gray-900 border-gray-200 hover:border-blue-300 hover:shadow-xl hover:shadow-blue-500/10'
                    } ${flippedCards.has(session.id) ? 'card-flipped' : ''}`}
                    onClick={() => toggleCardFlip(session.id, new Event('click') as React.MouseEvent<HTMLDivElement, MouseEvent>)}
                    style={{
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                    }}
                  >
                    <div className="card-inner">
                      {/* Card Front */}
                      <div className="card-front">
                        {/* Construction-themed header with icon */}
                        <div className={`p-3 ${projectColor} relative overflow-hidden`}>
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent to-white/10 opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                          {/* Construction pattern overlay */}
                          <div className="absolute inset-0 opacity-20" 
                               style={{
                                 backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.2) 10px, rgba(0,0,0,0.2) 20px)',
                                 zIndex: 0
                               }}>
                          </div>
                          <div className="flex items-center justify-between relative z-10">
                            <div className="flex items-center">
                              <div className={`p-2 rounded-lg ${darkMode ? 'bg-black bg-opacity-20' : 'bg-white bg-opacity-20'} shadow-inner relative`}>
                                <ProjectIcon className="h-5 w-5 text-white" />
                                {/* Small construction icon overlay */}
                                <div className="absolute -top-1 -right-1 bg-yellow-400 text-black rounded-full p-0.5 shadow-md">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              </div>
                              <div className="ml-2">
                                <h3 className="text-lg font-semibold text-white truncate bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-200">{projectName}</h3>
                                <div className="flex items-center text-xs text-white/80">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                  Blueprint
                                </div>
                              </div>
                            </div>
                            
                            {/* Flip button with construction theme */}
                            <button
                              onClick={(e) => toggleCardFlip(session.id, e)}
                              className={`p-1.5 rounded-full ${
                                darkMode ? 'bg-yellow-500 text-black' : 'bg-yellow-400 text-black'
                              } transition-all duration-200 shadow-md`}
                              aria-label="View blueprint details"
                              title="View blueprint details"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                              </svg>
                            </button>
                          </div>
                        </div>
                    
                        {/* Card body - Construction-themed design */}
                        <div className="p-4 flex flex-col h-[calc(100%-50px)] justify-between">
                          <div className="flex flex-col space-y-2 overflow-hidden">
                            {/* Construction banner */}
                            <div className={`mb-3 px-2 py-1 text-xs font-bold ${
                              darkMode 
                                ? 'bg-yellow-500 text-black' 
                                : 'bg-yellow-400 text-black'
                            } construction-banner relative overflow-visible`}>
                            <div className="absolute inset-0 bg-black opacity-20" 
                                 style={{
                                   backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.2) 10px, rgba(0,0,0,0.2) 20px)',
                                   zIndex: 0
                                 }}>
                            </div>
                            <div className="relative z-10 flex items-center justify-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M8.34 1.804A1 1 0 019.32 1h1.36a1 1 0 01.98.804l.295 1.473c.497.144.971.342 1.416.587l1.25-.834a1 1 0 011.262.125l.962.962a1 1 0 01.125 1.262l-.834 1.25c.245.445.443.919.587 1.416l1.473.294a1 1 0 01.804.98v1.361a1 1 0 01-.804.98l-1.473.295a6.95 6.95 0 01-.587 1.416l.834 1.25a1 1 0 01-.125 1.262l-.962.962a1 1 0 01-1.262.125l-1.25-.834a6.953 6.953 0 01-1.416.587l-.294 1.473a1 1 0 01-.98.804H9.32a1 1 0 01-.98-.804l-.295-1.473a6.957 6.957 0 01-1.416-.587l-1.25.834a1 1 0 01-1.262-.125l-.962-.962a1 1 0 01-.125-1.262l.834-1.25a6.957 6.957 0 01-.587-1.416l-1.473-.294A1 1 0 011 10.68V9.32a1 1 0 01.804-.98l1.473-.295c.144-.497.342-.971.587-1.416l-.834-1.25a1 1 0 01.125-1.262l.962-.962A1 1 0 015.38 3.03l1.25.834a6.957 6.957 0 011.416-.587l.294-1.473z" clipRule="evenodd" />
                              </svg>
                              BLUEPRINT IN PROGRESS
                            </div>
                          </div>
                          
                            {/* Project plan instead of description - with truncation instead of scrollbar */}
                            <div className={`mb-2 p-2 rounded ${darkMode ? 'bg-gray-700 bg-opacity-50' : 'bg-gray-100'} border-l-4 ${
                              darkMode ? 'border-yellow-500' : 'border-yellow-400'
                            }`}>
                            <h4 className="text-xs font-bold mb-1 flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M9 4.804A1 1 0 0110 4h.5a1 1 0 011 1v.5a1 1 0 01-1 1H10a1 1 0 01-1-1V4.804z" />
                                <path fillRule="evenodd" d="M2 2a2 2 0 012-2h4.5a2 2 0 012 2v15a2 2 0 01-2 2H4a2 2 0 01-2-2V2zm11 0a2 2 0 012-2h.5a2 2 0 012 2v15a2 2 0 01-2 2h-.5a2 2 0 01-2-2V2z" clipRule="evenodd" />
                              </svg>
                              PROJECT PLAN
                            </h4>
                            <div className="max-h-28 overflow-y-auto custom-scrollbar pr-1">
                              {session.manifest_content?.project_plan || session.project_plan || session.prompt ? (
                                <PersonaMarkdown 
                                  content={session.manifest_content?.project_plan || session.project_plan || session.prompt} 
                                  size="small" 
                                />
                              ) : (
                                <p className={`text-xs italic ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  No project plan available yet
                                </p>
                              )}
                            </div>
                          </div>
                      
                            {/* Tags section with construction theme */}
                            <div className="flex flex-wrap gap-1 mb-2">
                            {session.tags && session.tags.length > 0 ? (
                              session.tags.slice(0, 3).map((tag, index) => (
                                <span 
                                  key={`${session.id}-tag-${index}`} 
                                  className={`px-2 py-0.5 ${
                                    darkMode 
                                      ? 'bg-gray-700 text-gray-300 border border-gray-600' 
                                      : 'bg-gray-100 text-gray-700 border border-gray-200'
                                  } text-xs rounded-full`}
                                >
                                  {tag}
                                </span>
                              ))
                            ) : null}
                          </div>
                          
                            {/* Personas section - with max height to prevent overflow */}
                            {session.used_personas && session.used_personas.length > 0 && (
                              <div className="mb-2">
                                <h4 className="text-xs font-bold mb-1 flex items-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                  </svg>
                                  PERSONAS
                                </h4>
                                <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto custom-scrollbar">
                                {session.used_personas.slice(0, 5).map((persona, index) => (
                                  <span 
                                    key={`${session.id}-persona-${index}`} 
                                    className={`px-2 py-0.5 ${
                                      darkMode 
                                        ? 'bg-blue-900 text-blue-300 border border-blue-800' 
                                        : 'bg-blue-100 text-blue-800 border border-blue-200'
                                    } text-xs rounded-full`}
                                    title={persona}
                                  >
                                    {persona.replace(/_/g, ' ')}
                                  </span>
                                ))}
                                {session.used_personas.length > 5 && (
                                  <span className={`px-2 py-0.5 ${
                                    darkMode 
                                      ? 'bg-gray-700 text-gray-300' 
                                      : 'bg-gray-200 text-gray-700'
                                  } text-xs rounded-full`}>
                                    +{session.used_personas.length - 5} more
                                  </span>
                                )}
                                </div>
                              </div>
                            )}
                          </div>
                      
                          {/* Project details with construction theme */}
                          <div className="grid grid-cols-4 gap-2 text-xs">
                            <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-2 ${
                              darkMode ? 'border-yellow-500' : 'border-yellow-400'
                            }`}>
                              <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                <span className="font-medium flex items-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                  </svg>
                                  Created:
                                </span>
                              </div>
                              <div className="font-semibold">{timeAgo}</div>
                            </div>
                            <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-2 ${
                              darkMode ? 'border-yellow-500' : 'border-yellow-400'
                            }`}>
                              <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                <span className="font-medium flex items-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                  </svg>
                                  Status:
                                </span>
                              </div>
                              <div className="font-semibold">{getProjectStatus(session)}</div>
                            </div>
                            <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-2 ${
                              darkMode ? 'border-yellow-500' : 'border-yellow-400'
                            }`}>
                              <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                <span className="font-medium flex items-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M9 4.804A1 1 0 0110 4h.5a1 1 0 011 1v.5a1 1 0 01-1 1H10a1 1 0 01-1-1V4.804z" />
                                    <path fillRule="evenodd" d="M2 2a2 2 0 012-2h4.5a2 2 0 012 2v15a2 2 0 01-2 2H4a2 2 0 01-2-2V2zm11 0a2 2 0 012-2h.5a2 2 0 012 2v15a2 2 0 01-2 2h-.5a2 2 0 01-2-2V2z" clipRule="evenodd" />
                                  </svg>
                                  Files:
                                </span>
                              </div>
                              <div className="font-semibold">
                                {session.manifest_content?.nodes ? Object.keys(session.manifest_content.nodes).filter(
                                  id => session.manifest_content.nodes[id].type === 'file'
                                ).length : 0}
                              </div>
                            </div>
                            <div className={`p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-50'} border-l-2 ${
                              darkMode ? 'border-yellow-500' : 'border-yellow-400'
                            }`}>
                              <div className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                <span className="font-medium flex items-center">
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                  </svg>
                                  Hydration:
                                </span>
                              </div>
                              <div className="font-semibold">
                                {(() => {
                                  // Calculate hydration percentage using the same logic as MiniFileTree
                                  const stats = {
                                    total: 0,
                                    fresh: 0,
                                    stale: 0,
                                    ghost: 0
                                  };
                                  
                                  if (session.manifest_content?.nodes) {
                                    Object.values(session.manifest_content.nodes).forEach((node: any) => {
                                      if (node.type === 'file') {
                                        stats.total++;
                                        if (node.status === 'fresh') stats.fresh++;
                                        else if (node.status === 'stale') stats.stale++;
                                        else if (node.status === 'ghost') stats.ghost++;
                                      }
                                    });
                                  }
                                  
                                  const hydrationPercentage = stats.total > 0 
                                    ? Math.round((stats.fresh / stats.total) * 100) 
                                    : 0;
                                  
                                  return `${hydrationPercentage}%`;
                                })()}
                              </div>
                            </div>
                          </div>
                          
                          {/* Add hydration progress bar */}
                          <div className="mt-3">
                            <div className={`h-2 w-full rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                              <div 
                                className={`h-full rounded-full ${(() => {
                                  // Calculate hydration percentage for color
                                  const stats = {
                                    total: 0,
                                    fresh: 0
                                  };
                                  
                                  if (session.manifest_content?.nodes) {
                                    Object.values(session.manifest_content.nodes).forEach((node: any) => {
                                      if (node.type === 'file') {
                                        stats.total++;
                                        if (node.status === 'fresh') stats.fresh++;
                                      }
                                    });
                                  }
                                  
                                  const hydrationPercentage = stats.total > 0 
                                    ? Math.round((stats.fresh / stats.total) * 100) 
                                    : 0;
                                  
                                  // Color based on percentage
                                  if (hydrationPercentage >= 80) return darkMode ? 'bg-green-600' : 'bg-green-500';
                                  if (hydrationPercentage >= 50) return darkMode ? 'bg-blue-600' : 'bg-blue-500';
                                  if (hydrationPercentage >= 25) return darkMode ? 'bg-yellow-600' : 'bg-yellow-500';
                                  return darkMode ? 'bg-red-600' : 'bg-red-500';
                                })()}`}
                                style={{ 
                                  width: (() => {
                                    // Calculate width percentage
                                    const stats = {
                                      total: 0,
                                      fresh: 0
                                    };
                                    
                                    if (session.manifest_content?.nodes) {
                                      Object.values(session.manifest_content.nodes).forEach((node: any) => {
                                        if (node.type === 'file') {
                                          stats.total++;
                                          if (node.status === 'fresh') stats.fresh++;
                                        }
                                      });
                                    }
                                    
                                    const hydrationPercentage = stats.total > 0 
                                      ? Math.round((stats.fresh / stats.total) * 100) 
                                      : 0;
                                    
                                    return `${hydrationPercentage}%`;
                                  })()
                                }}
                              ></div>
                            </div>
                          </div>
                      
                          {/* Footer with construction-themed action buttons */}
                          <div className="mt-3 pt-2 flex justify-between items-center border-t border-gray-200 dark:border-gray-700">
                            <span className={`text-xs ${darkMode ? 'text-yellow-400' : 'text-yellow-600'} flex items-center`}>
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                              </svg>
                              Tap to flip
                            </span>
                            <button 
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent card flip
                                handleSessionSelect(session.id);
                              }}
                              className={`px-3 py-1 text-xs rounded flex items-center ${
                                darkMode 
                                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                  : 'bg-blue-500 hover:bg-blue-600 text-white'
                              } z-20 font-bold shadow-md`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                              </svg>
                              Open Blueprint
                            </button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Card Back - Stop propagation for all clicks */}
                      <div className="card-back" onClick={(e) => e.stopPropagation()}>
                        <div className="p-4 h-full flex flex-col space-y-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-between items-center mb-3" onClick={(e) => e.stopPropagation()}>
                            <h3 className="text-lg font-semibold" onClick={(e) => e.stopPropagation()}>{projectName}</h3>
                            <button
                              onClick={(e) => {
                                console.log("Back button clicked for session:", session.id);
                                e.stopPropagation(); // Prevent card's onClick from firing
                                toggleCardFlip(session.id, e);
                              }}
                              className={`p-2 rounded-full ${
                                darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'
                              } transition-all duration-200 z-50 relative`}
                              aria-label="Back to front"
                              title="Back to front"
                              style={{ position: 'relative', zIndex: 50 }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" style={{ pointerEvents: 'none' }}>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                              </svg>
                            </button>
                          </div>
                          
                          {/* Mini file tree - with stopPropagation */}
                          <div className="flex-1 overflow-hidden" onClick={(e) => e.stopPropagation()}>
                            <MiniFileTree 
                              manifestId={session.manifest_id} 
                              maxHeight="150px"
                              onDependencyClick={(nodeId) => {
                                console.log("Dependency click handler called with nodeId:", nodeId);
                                // Navigate to the artifactory page with the selected node
                                console.log("Calling handleSessionSelect with session.id:", session.id);
                                handleSessionSelect(session.id);
                                // The node will be selected after navigation completes
                                console.log("Setting localStorage artifactory_selectedNodeId to:", nodeId);
                                localStorage.setItem('artifactory_selectedNodeId', nodeId);
                              }}
                            />
                          </div>
                          
                          {/* Actions - with stopPropagation */}
                          <div className="mt-3 flex justify-between items-center" onClick={(e) => e.stopPropagation()}>
                            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} onClick={(e) => e.stopPropagation()}>
                              Last updated: {new Date(session.updated_at).toLocaleDateString()}
                            </div>
                            
                            <button 
                              onClick={(e) => {
                                e.stopPropagation(); // Ensure propagation is stopped
                                handleSessionSelect(session.id);
                              }}
                              className={`px-3 py-1 text-xs rounded flex items-center ${
                                darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                              } z-20 font-bold`}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                              </svg>
                              Open Blueprint
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            ) : (
              // Table view
              <div className={`overflow-x-auto rounded-lg border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <table className={`w-full divide-y ${darkMode ? 'divide-gray-700 bg-gray-800 text-gray-200' : 'divide-gray-200 bg-white text-gray-800'}`}>
                  <thead className={darkMode ? 'bg-gray-900' : 'bg-gray-50'}>
                    <tr>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('name')}
                      >
                        <div className="flex items-center">
                          Project Name
                          {sortField === 'name' && (
                            <span className="ml-1">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                        Status
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('created')}
                      >
                        <div className="flex items-center">
                          Created
                          {sortField === 'created' && (
                            <span className="ml-1">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer"
                        onClick={() => handleSort('hydration')}
                      >
                        <div className="flex items-center">
                          Hydration
                          {sortField === 'hydration' && (
                            <span className="ml-1">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    {(sortField ? sortedSessions : sessions).map((session) => {
                      // Extract project name from session data
                      const projectName = session.project_name || 
                                        (session.prompt ? session.prompt.substring(0, 30) + '...' : 'Untitled Project');
                  
                      // Calculate time since creation for display
                      const createdDate = new Date(session.created_at);
                      const timeAgo = getTimeAgo(createdDate);
                  
                      // Determine project icon and color based on metadata or content
                      const { icon: ProjectIcon, color: projectColor } = getProjectIconAndColor(session);
                  
                      // Get project status
                      const status = getProjectStatus(session);
                      
                      return (
                        <tr 
                          key={session.id}
                          className={`hover:${darkMode ? 'bg-gray-700' : 'bg-gray-50'} cursor-pointer transition-colors duration-150`}
                          onClick={() => handleSessionSelect(session.id)}
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className={`p-2 rounded-lg ${color2BgClass(projectColor)} relative overflow-hidden`}>
                                {/* Construction pattern overlay */}
                                <div className="absolute inset-0 opacity-20" 
                                     style={{
                                       backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.2) 10px, rgba(0,0,0,0.2) 20px)',
                                       zIndex: 0
                                     }}>
                                </div>
                                <ProjectIcon className="h-4 w-4 text-white relative z-10" />
                              </div>
                              <div className="ml-3">
                                <div className="font-medium">{projectName}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  Blueprint ID: {session.id.substring(0, 8)}...
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              status === 'Completed'
                                ? darkMode 
                                  ? 'bg-green-900 text-green-300 border border-green-700' 
                                  : 'bg-green-100 text-green-800 border border-green-200'
                                : status === 'Active'
                                  ? darkMode 
                                    ? 'bg-blue-900 text-blue-300 border border-blue-700' 
                                    : 'bg-blue-100 text-blue-800 border border-blue-200'
                                  : darkMode 
                                    ? 'bg-gray-700 text-gray-300 border border-gray-600' 
                                    : 'bg-gray-100 text-gray-800 border border-gray-200'
                            }`}>
                              {status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className="flex flex-col">
                              <span>{timeAgo}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {new Date(session.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col space-y-1">
                              <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                (() => {
                                  // Calculate hydration percentage
                                  const stats = {
                                    total: 0,
                                    fresh: 0
                                  };
                  
                                  if (session.manifest_content?.nodes) {
                                    Object.values(session.manifest_content.nodes).forEach((node: any) => {
                                      if (node.type === 'file') {
                                        stats.total++;
                                        if (node.status === 'fresh') stats.fresh++;
                                      }
                                    });
                                  }
                  
                                  const hydrationPercentage = stats.total > 0 
                                    ? Math.round((stats.fresh / stats.total) * 100) 
                                    : 0;
                  
                                  // Color based on percentage
                                  if (hydrationPercentage >= 80) return darkMode ? 'bg-green-900 text-green-300 border border-green-700' : 'bg-green-100 text-green-800 border border-green-200';
                                  if (hydrationPercentage >= 50) return darkMode ? 'bg-blue-900 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-800 border border-blue-200';
                                  if (hydrationPercentage >= 25) return darkMode ? 'bg-yellow-900 text-yellow-300 border border-yellow-700' : 'bg-yellow-100 text-yellow-800 border border-yellow-200';
                                  return darkMode ? 'bg-red-900 text-red-300 border border-red-700' : 'bg-red-100 text-red-800 border border-red-200';
                                })()
                              }`}>
                                {(() => {
                                  // Calculate hydration percentage
                                  const stats = {
                                    total: 0,
                                    fresh: 0
                                  };
                  
                                  if (session.manifest_content?.nodes) {
                                    Object.values(session.manifest_content.nodes).forEach((node: any) => {
                                      if (node.type === 'file') {
                                        stats.total++;
                                        if (node.status === 'fresh') stats.fresh++;
                                      }
                                    });
                                  }
                  
                                  const hydrationPercentage = stats.total > 0 
                                    ? Math.round((stats.fresh / stats.total) * 100) 
                                    : 0;
                  
                                  return `${hydrationPercentage}%`;
                                })()}
                              </span>
                              <div className={`h-1.5 w-24 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                                <div 
                                  className={`h-full rounded-full ${(() => {
                                    // Calculate hydration percentage for color
                                    const stats = {
                                      total: 0,
                                      fresh: 0
                                    };
                    
                                    if (session.manifest_content?.nodes) {
                                      Object.values(session.manifest_content.nodes).forEach((node: any) => {
                                        if (node.type === 'file') {
                                          stats.total++;
                                          if (node.status === 'fresh') stats.fresh++;
                                        }
                                      });
                                    }
                    
                                    const hydrationPercentage = stats.total > 0 
                                      ? Math.round((stats.fresh / stats.total) * 100) 
                                      : 0;
                    
                                    // Color based on percentage
                                    if (hydrationPercentage >= 80) return darkMode ? 'bg-green-600' : 'bg-green-500';
                                    if (hydrationPercentage >= 50) return darkMode ? 'bg-blue-600' : 'bg-blue-500';
                                    if (hydrationPercentage >= 25) return darkMode ? 'bg-yellow-600' : 'bg-yellow-500';
                                    return darkMode ? 'bg-red-600' : 'bg-red-500';
                                  })()}`}
                                  style={{ 
                                    width: (() => {
                                      // Calculate width percentage
                                      const stats = {
                                        total: 0,
                                        fresh: 0
                                      };
                      
                                      if (session.manifest_content?.nodes) {
                                        Object.values(session.manifest_content.nodes).forEach((node: any) => {
                                          if (node.type === 'file') {
                                            stats.total++;
                                            if (node.status === 'fresh') stats.fresh++;
                                          }
                                        });
                                      }
                      
                                      const hydrationPercentage = stats.total > 0 
                                        ? Math.round((stats.fresh / stats.total) * 100) 
                                        : 0;
                      
                                      return `${hydrationPercentage}%`;
                                    })()
                                  }}
                                ></div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex space-x-2">
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSessionSelect(session.id);
                                }}
                                className={`px-3 py-1 text-xs rounded flex items-center ${
                                  darkMode 
                                    ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                                } shadow-sm`}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                                </svg>
                                Open
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            <div className={`text-center py-16 rounded-lg ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
              <div className="flex justify-center mb-4">
                <div className={`p-4 rounded-full ${darkMode ? 'bg-blue-900' : 'bg-blue-100'}`}>
                  <FiCode className={`h-10 w-10 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`} />
                </div>
              </div>
              <h3 className="text-xl font-semibold mb-2">No projects found</h3>
              <p className={`mb-6 max-w-md mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Create your first Artifactory project to start generating code with AI
              </p>
              <div className="group relative inline-block mx-auto">
                <button 
                  onClick={handleCreateNewProject}
                  className={`${
                    darkMode 
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700' 
                      : 'bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 to-purple-600'
                  } 
                  text-white px-6 py-3 rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center mx-auto
                  hover:shadow-blue-500/50 dark:hover:shadow-blue-800/30`}
                >
                  <FiPlus className="mr-2" />
                  New Blueprint
                  <span className="ml-2"><BetaBadge /></span>
                </button>
              </div>
            </div>
          )}
        </div>
      );
      
    case 'SCAFFOLDING':
      return (
        <div className={`w-full ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center">
              <button 
                onClick={handleNewArtifactoryProject}
                className={`mr-4 p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
              >
                <FiArrowLeft />
              </button>
              <h1 className="text-3xl font-bold">Create Project Structure</h1>
            </div>
          </div>
          
          {selectedSessionId && (
            <ScaffoldingForm 
              sessionId={selectedSessionId} 
              onComplete={handleScaffoldingComplete} 
            />
          )}
        </div>
      );
      
    case 'PROJECT_VIEW':
      return (
        <div 
          className={`grid grid-rows-[auto_auto_1fr_auto] h-screen ${darkMode ? 'text-white bg-gray-900' : 'text-gray-800 bg-gray-100'}`}
          style={{ overflow: 'hidden' }}
        >
          {/* Header - fixed height with grid positioning */}
          <header className={`py-1 px-2 border-b ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="container mx-auto flex justify-between items-center">
              <div className="flex items-center">
                <h1 className="text-2xl font-bold">Artifactory Project</h1>
              </div>
            
              <div className="flex space-x-2">
                <button 
                  className={`px-3 py-1 rounded flex items-center ${darkMode ? 'bg-gray-700 cursor-not-allowed opacity-60' : 'bg-gray-200 cursor-not-allowed opacity-60'}`}
                  disabled={true}
                  title="Coming soon..."
                >
                  <FiGitBranch className="mr-1" />
                  Branch
                </button>
                <button 
                  className={`px-3 py-1 rounded flex items-center ${darkMode ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                  onClick={() => {
                    if (manifestId) {
                      // First select the root node to show the special panel
                      const rootNodeId = Object.keys(manifest?.nodes || {}).find(
                        id => {
                          const path = manifest?.nodes[id].path;
                          return path === '/' || path === '' || path === '.';
                        }
                      );
                      
                      if (rootNodeId) {
                        selectNode(rootNodeId);
                      } else {
                        // Fallback if no root node found
                        const url = `/api/artifactory/export/${manifestId}?include_stale=true&include_ghost=true`;
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `project.zip`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }
                  }}
                  disabled={!manifestId}
                >
                  <FiDownload className="mr-1" />
                  Export
                </button>
                <button
                  className={`px-3 py-1 rounded flex items-center ${
                    !manifestId
                      ? darkMode ? 'bg-gray-700 cursor-not-allowed opacity-60' : 'bg-gray-200 cursor-not-allowed opacity-60'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                  disabled={!manifestId}
                  onClick={() => setHandoffDialogOpen(true)}
                  title="Handoff project to an external AI coding agent"
                >
                  <FiCpu className="mr-1 flex-shrink-0" />
                  <span className="text-center leading-tight text-xs">Handoff to<br />Ext. Agent</span>
                </button>
                {handoffDialogOpen && manifestId && (
                  <HandoffDialog
                    isOpen={handoffDialogOpen}
                    onClose={() => setHandoffDialogOpen(false)}
                    manifestId={manifestId}
                  />
                )}
                <button 
                  onClick={handleNewArtifactoryProject}
                  className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                >
                  New Project
                </button>
                <button 
                  onClick={() => navigate('/artifactory')}
                  className={`p-2 rounded-full ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                  title="Back to Projects"
                >
                  <FiArrowLeft />
                </button>
              </div>
            </div>
          </header>
        
          {/* Project Details Panel - new addition */}
          <div className="container mx-auto px-4 py-2">
            <ProjectDetailsPanel 
              intent={sessionData?.prompt || ''}
              originalIntent={manifest?.original_intent}
              projectPlan={manifest?.project_plan}
              manifestId={manifestId}
              sessionId={selectedSessionId}
              createdAt={sessionData?.created_at}
              updatedAt={sessionData?.updated_at}
              tags={sessionData?.tags || []}
              intentHistory={manifest?.intent_history || []}
              manifest={manifest} // Add this prop
              onIntentUpdate={handleUpdateIntent}
            />
          </div>
        
          {/* Main Content - Three Panel Layout with grid positioning */}
          <div className="grid grid-cols-4 min-h-0" style={{ overflow: 'hidden' }}>
            {/* Left Panel - Chat Interface */}
            <div className={`border-r ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex flex-col min-h-0 overflow-hidden`}>
              <ArtifactoryChat 
                sessionId={selectedSessionId || ''} 
                manifestId={manifestId || ''}
                initialPrompt={sessionData?.prompt || ''}
                onUpdateIntent={handleUpdateIntent}
              />
            </div>
            
            {/* Middle Panel - File Browser */}
            <div className={`border-r ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex flex-col min-h-0 overflow-hidden`}>
              <div className="flex flex-col min-h-0 flex-1 overflow-hidden">
                <div className={`py-1 px-2 border-b flex justify-between items-center ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-100 border-gray-200'}`}>
                  <h2 className="font-semibold text-sm">Project Files</h2>
              
                  {/* Project Tools button */}
                  <button
                    onClick={() => {
                      console.log("Project Tools button clicked");
                      
                      // Find the root node ID by identifying the node that has no parent
                      // or is the top-level directory in the hierarchy
                      const findRootNode = () => {
                        if (!manifest || !manifest.nodes) return null;
                        
                        // Find directory nodes that aren't contained within other directories
                        const rootCandidates = Object.entries(manifest.nodes)
                          .filter(([_, node]) => node.type === 'directory')
                          .filter(([_, node]) => {
                            // A node is a root candidate if its path isn't a subdirectory of any other node
                            const nodePath = node.path;
                            // Check if this path is a subdirectory of any other directory
                            return !Object.values(manifest.nodes).some(otherNode => 
                              otherNode.type === 'directory' && 
                              otherNode.path !== nodePath && 
                              nodePath.startsWith(otherNode.path + '/')
                            );
                          });
                        
                        // If we have candidates, return the one with the shortest path
                        if (rootCandidates.length > 0) {
                          rootCandidates.sort((a, b) => a[1].path.length - b[1].path.length);
                          return rootCandidates[0][0]; // Return the ID of the root node
                        }
                        
                        return null;
                      };
                      
                      const rootNodeId = findRootNode();
                      console.log("Found root node ID:", rootNodeId);
                      
                      if (rootNodeId) {
                        console.log("Calling selectNode with rootNodeId:", rootNodeId);
                        selectNode(rootNodeId);
                      } else {
                        console.log("No root node found in manifest");
                        console.log("Available nodes:", Object.keys(manifest?.nodes || {}));
                        console.log("Manifest:", manifest);
                      }
                    }}
                    className={`px-2 py-1 text-xs rounded flex items-center ${
                      darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                    title="Project Tools"
                  >
                    <FiSettings className="mr-1" />
                    Project Tools
                  </button>
                </div>
                <div className="flex-1 overflow-auto min-h-0 h-0">
                  <FileTree 
                    manifest={manifest} 
                    selectedNodeId={selectedNodeId} 
                    onSelectNode={(nodeId) => {
                      console.log("FileTree onSelectNode called with:", nodeId);
                      selectNode(nodeId);
                    }} 
                  />
                </div>
                {/* Add the status log component */}
                <ProjectStatusLog 
                  entries={statusLogs}
                  onClearEntry={(id) => setStatusLogs(prev => prev.filter(entry => entry.id !== id))}
                  onClearAll={() => setStatusLogs([])}
                />
              </div>
            </div>
            
            {/* Right Panel - Node Inspector (spans 2 columns) */}
            <div className="col-span-2 min-h-0 overflow-hidden">
              <NodeInspector sessionId={selectedSessionId} />
            </div>
          </div>
          
          {/* Footer - fixed height with grid positioning */}
          <footer className={`py-1 px-2 border-t ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            <div className="container mx-auto flex justify-between items-center text-sm">
              <div className="flex items-center">
                <FiCode className="mr-2" />
                <span>Artifactory v1.0</span>
              </div>
              <div>
                Session ID: <span className="font-mono">{selectedSessionId?.substring(0, 8)}...</span>
              </div>
            </div>
          </footer>
          
          {/* Hidden component to load the manifest */}
          {manifestId && selectedSessionId && (
            <ArtifactoryCommander 
              sessionId={selectedSessionId} 
              manifestId={manifestId} 
            />
          )}
        </div>
      );
      
    default:
      return null;
  }
};
